import { IconChevronRight, IconClock, IconListDetails } from '@tabler/icons-react';
import type { RidesServiceTimeView } from '@actschurch/ccms-sdk';
import { formatTime } from '@/lib/format';
import { Text } from '@mantine/core';
import { PickerCard, PickerGroup } from '@/components/ui/PickerCard';
import '@/styles/flows.css';

interface Props {
  times: RidesServiceTimeView[];
  value: number | null;
  onChange: (serviceTimeId: number | null) => void;
  /** Offer an "Any service" choice (riders). */
  allowAny?: boolean;
  campusName?: string;
  /** chips (default, compact: security desk) or cards (one big card per service, for the step flows). */
  variant?: 'chips' | 'cards';
  /** Cards only: title and help line of the "Any service" card. */
  anyLabel?: string;
  anyDescription?: string;
  /** Cards only: choosing moves to the next screen (chevron instead of a radio circle). */
  navigates?: boolean;
}

/** "First service" for "First Service"; nothing when the name only repeats the time. */
function serviceName(t: RidesServiceTimeView): string {
  const name = t.name?.trim();
  if (!name || name === formatTime(t.startTime)) return '';
  return name.charAt(0) + name.slice(1).toLowerCase();
}

/** Service times of the drop-off campus: compact chips, or big cards in the step flows. */
export function ServiceTimePicker({ times, value, onChange, allowAny, campusName, variant = 'chips', anyLabel = 'Any service', anyDescription, navigates }: Props) {
  if (!times.length) {
    return (
      <Text size="sm" c="dimmed">
        Service times for <b>{campusName ?? 'this campus'}</b> aren't set yet. The church office will confirm them.
      </Text>
    );
  }

  if (variant === 'cards') {
    const chev = navigates ? <span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span> : undefined;
    return (
      <PickerGroup ariaLabel="Service time">
        {times.map((t) => (
          <PickerCard
            key={t.serviceTimeID}
            selected={value === t.serviceTimeID}
            onSelect={() => onChange(t.serviceTimeID)}
            title={formatTime(t.startTime)}
            description={serviceName(t) || undefined}
            media={navigates ? <span className="rides-icontile" aria-hidden="true"><IconClock size={22} /></span> : undefined}
            hideIndicator={navigates}
            trailing={chev}
          />
        ))}
        {allowAny && (
          <PickerCard
            selected={value == null}
            onSelect={() => onChange(null)}
            title={anyLabel}
            description={anyDescription}
            media={navigates ? <span className="rides-icontile warm" aria-hidden="true"><IconListDetails size={22} /></span> : undefined}
            hideIndicator={navigates}
            trailing={chev}
          />
        )}
      </PickerGroup>
    );
  }

  return (
    <div className="rides-chiprow" role="radiogroup" aria-label="Service time">
      {allowAny && (
        <button type="button" role="radio" className="rides-chip" aria-checked={value == null} onClick={() => onChange(null)}>
          {anyLabel}
        </button>
      )}
      {times.map((t) => (
        <button
          key={t.serviceTimeID}
          type="button"
          role="radio"
          className="rides-chip"
          aria-checked={value === t.serviceTimeID}
          onClick={() => onChange(t.serviceTimeID)}
        >
          {formatTime(t.startTime)}
        </button>
      ))}
    </div>
  );
}
